import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getNSectionVO,
    saveNSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    N2_PAGE,
    NAPP01_PAGE,
    NAPP02_PAGE,
    NS_PAGE,
    N_PAGE_TITLES,
    N_SECTION_INDEX,
    N_SECTION_INDEX_LETTER,
    N_SECTION_INDEX_TITLE,
    N_SECTION_PAGES,
    NProgrammePage,
    NSectionPageCode,
    NSummaryPage,
} from '@/pages/components/audit-program/n-section/constants';
import NsSummaryView from '@/pages/components/audit-program/n-section/NsSummaryView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type NSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: NSectionPageCode;
};

const DISPLAY_ONLY_PAGES = new Set<NSectionPageCode>(['Ni']);

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

function isProgramme(code: NSectionPageCode): boolean {
    return code === 'N2' || code === 'Napp01' || code === 'Napp02';
}

export default function NSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Ni',
}: NSectionViewProps) {
    const [pageKey, setPageKey] = useState<NSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('N');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getNSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of N_SECTION_PAGES) {
                if (code === 'Ni') {
                    pages[code] = toStringRecord(data.pages?.[code]);
                    continue;
                }
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load N Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: NSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveNSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const indexRows = useMemo(() => {
        const fromApi = getContent('Ni') as {rows?: typeof N_SECTION_INDEX} | undefined;
        return fromApi?.rows?.length ? fromApi.rows : N_SECTION_INDEX;
    }, [getContent]);

    const nsContent = (getContent('Ns') ?? NS_PAGE) as NSummaryPage;
    const n2Content = (getContent('N2') ?? N2_PAGE) as NProgrammePage;
    const napp01Content = (getContent('Napp01') ?? NAPP01_PAGE) as NProgrammePage;
    const napp02Content = (getContent('Napp02') ?? NAPP02_PAGE) as NProgrammePage;

    const programmeContent = (code: NSectionPageCode): NProgrammePage => {
        if (code === 'Napp01') return napp01Content;
        if (code === 'Napp02') return napp02Content;
        return n2Content;
    };

    const tabs = useMemo(() => N_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={N_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Ni' ? (
                <SectionIndexView
                    header={header}
                    refCode="Ni"
                    sectionLetter={N_SECTION_INDEX_LETTER}
                    sectionTitle={N_SECTION_INDEX_TITLE}
                    rows={indexRows}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as NSectionPageCode))}
                />
            ) : code === 'Ns' ? (
                <NsSummaryView
                    header={header}
                    page={nsContent}
                    refCode="Ns"
                    values={pageData.Ns ?? {}}
                    onChange={(k, v) => handleFieldChange('Ns', k, v)}
                />
            ) : isProgramme(code) ? (
                <G0ProgrammeView
                    header={header}
                    page={programmeContent(code)}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            ) : null
        ) : null,
    })), [header, pageData, handleFieldChange, indexRows, nsContent, n2Content, napp01Content, napp02Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const saveDisabled = DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey] || saving;

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    N Section — Provisions, contingent liabilities and financial commitments
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={saveDisabled}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as NSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
